/**
 * TypeChip Component
 *
 * Shared type badge/chip for displaying column types consistently
 * across input mapping, output mapping, and column selector.
 */

import { Tooltip } from '@affine/component';
import { STREAMLAIN_TYPE_CHIP_LAYOUT } from '@blocksuite/affine-shared/styles';
import {
  getTypeColor,
  normalizeTypeLabel,
} from '@blocksuite/affine-shared/utils';
import type { FC } from 'react';

// Re-export for backwards compatibility
export { getTypeColor, normalizeTypeLabel };

interface TypeChipProps {
  type: string;
  /** Use smaller size for compact displays */
  small?: boolean;
  /** Override inline styles (e.g. remove marginLeft in flex containers) */
  style?: React.CSSProperties;
  /** Click handler (e.g. cycle type in editor) */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * TypeChip - displays a type badge with consistent styling
 *
 * Style: Solid background with white text, uppercase
 */
export const TypeChip: FC<TypeChipProps> = ({
  type,
  small = false,
  style: overrideStyle,
  onClick,
}) => {
  const color = getTypeColor(type);
  const displayType = normalizeTypeLabel(type);

  return (
    <span
      data-streamlain-type-chip
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{
        ...STREAMLAIN_TYPE_CHIP_LAYOUT,
        fontSize: small ? '9px' : STREAMLAIN_TYPE_CHIP_LAYOUT.fontSize,
        minHeight: small ? '14px' : STREAMLAIN_TYPE_CHIP_LAYOUT.minHeight,
        padding: small ? '0px 3px' : STREAMLAIN_TYPE_CHIP_LAYOUT.padding,
        lineHeight: small ? '14px' : STREAMLAIN_TYPE_CHIP_LAYOUT.lineHeight,
        backgroundColor: color,
        marginLeft: '4px',
        ...overrideStyle,
      }}
    >
      {displayType}
    </span>
  );
};

// =============================================================================
// CompatDot — shared compatibility indicator circle
// =============================================================================

const COMPAT_COLORS = {
  compatible: '#4CAF50',
  castable: '#FF9800',
  incompatible: '#F44336',
} as const;

const COMPAT_LABELS = {
  compatible: '✓',
  castable: '~',
  incompatible: '!',
} as const;

const COMPAT_TOOLTIPS = {
  compatible: 'Types are compatible',
  castable: 'Types will be auto-cast',
  incompatible: 'Types may not be compatible',
} as const;

interface CompatDotProps {
  compatibility: 'compatible' | 'castable' | 'incompatible';
  /** Override tooltip text (default: generic message) */
  tooltip?: string;
  /** Override inline styles */
  style?: React.CSSProperties;
}

/**
 * CompatDot — 14x14 colored circle with ✓/~/! symbol and tooltip.
 * Shared across sidebar rows, dropdown options, and dock mapper.
 */
export const CompatDot: FC<CompatDotProps> = ({
  compatibility,
  tooltip,
  style: overrideStyle,
}) => {
  return (
    <Tooltip content={tooltip ?? COMPAT_TOOLTIPS[compatibility]}>
      <span
        style={{
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          backgroundColor: COMPAT_COLORS[compatibility],
          color: 'white',
          fontSize: '9px',
          fontWeight: 'bold',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          cursor: 'help',
          ...overrideStyle,
        }}
      >
        {COMPAT_LABELS[compatibility]}
      </span>
    </Tooltip>
  );
};
